import { useMemo, useState } from 'react';
import { Button, Stack, TextField } from '@mui/material';
import _ from 'lodash';

import { GameElementPicker } from '../../shared/GameElementPicker/GameElementPicker';
import { AdvancedOptions } from './giftFormParts/AdvancedOptions';
import { PublicationsSection } from './giftFormParts/PublicationsSection';
import { WrappingField } from './giftFormParts/WrappingField';

import { GameElementDto } from '../../hooks/gameElements';
import { GiftSchemas } from '../../hooks/useGiftApi';

import { assert } from '../../utils/assert';

import { Gift, Publication, PublicationWindow } from './giftFormParts/types';

import { PLACEHOLDER_GIFT } from './giftFormParts/giftFormUtils';
import { usePublicationDrafts } from './giftFormParts/usePublicationDrafts';

export interface GiftFormValue {
    id: string;
    name: string;
    contents: string[];
    wrapperGameElementId: string;
    tutorial: boolean;
    defaultClaimed: boolean;
    minimumVersion: string | null;
    jiraIssueKey: string | null;
    /** Only set when creating a gift; existing gifts are scheduled via publications. */
    publication?: PublicationWindow;
}

interface GiftFormProps {
    gameElements: Map<string, GameElementDto>;
    defaultValue?: GiftSchemas['FullGiftDTO'];
    publications?: GiftSchemas['FullGiftAdminItem']['publications'];
    onSave?: (value: GiftFormValue) => Promise<unknown>;
    onCreatePublication?: (window: PublicationWindow) => Promise<Publication>;
    onUpdatePublication?: (
        publicationId: string,
        window: PublicationWindow,
    ) => Promise<void>;
    onDeletePublication?: (publicationId: string) => Promise<void>;
    onCancel?: () => void;
}

export const GiftForm = ({
    gameElements,
    defaultValue,
    publications,
    onSave,
    onCreatePublication,
    onUpdatePublication,
    onDeletePublication,
    onCancel,
}: GiftFormProps) => {
    const [baseline, setBaseline] = useState<Gift>(() =>
        defaultValue ? { ...defaultValue } : PLACEHOLDER_GIFT,
    );
    const [gift, setGift] = useState(baseline);
    const isNewGift = !defaultValue?.id;

    const publicationState = usePublicationDrafts({
        publications,
        isNewGift,
        onCreatePublication,
        onUpdatePublication,
        onDeletePublication,
    });

    const updateGift = (patch: Partial<Gift>) =>
        setGift((current) => ({ ...current, ...patch }));

    const selectedGameElements = useMemo(
        () =>
            gift.contents
                ?.map((gameElementId) => gameElements.get(gameElementId))
                .filter((element) => element !== undefined) ?? [],
        [gift.contents, gameElements],
    );

    const isUnsaved = useMemo(
        () => !_.isEqual(gift, baseline),
        [gift, baseline],
    );

    const initialPublication = publicationState.drafts[0];
    const missingDates =
        isNewGift &&
        (!initialPublication?.startDate || !initialPublication?.endDate);

    const saveGift = async () => {
        if (!onSave || missingDates) {
            return;
        }

        const {
            name,
            contents = [],
            wrapperGameElementId,
            tutorial = false,
            defaultClaimed = false,
            minimumVersion,
            jiraIssueKey,
        } = gift;

        assert(name, 'name is undefined');
        assert(wrapperGameElementId, 'wrapperGameElementId is undefined');
        assert(contents, 'contents is undefined');

        const id = gift.id ?? crypto.randomUUID();

        await onSave({
            name,
            contents,
            wrapperGameElementId,
            id,
            tutorial,
            defaultClaimed,
            minimumVersion: minimumVersion ?? null,
            jiraIssueKey: jiraIssueKey ?? null,
            ...(isNewGift &&
            initialPublication?.startDate &&
            initialPublication.endDate
                ? {
                      publication: {
                          startDate: new Date(
                              initialPublication.startDate,
                          ).toISOString(),
                          endDate: new Date(
                              initialPublication.endDate,
                          ).toISOString(),
                      },
                  }
                : {}),
        });

        const saved = { ...gift, id };
        setGift(saved);
        setBaseline(saved);
    };

    const cancel = () => {
        onCancel?.();
        setGift(baseline);
    };

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                saveGift();
            }}
        >
            <Stack sx={{ margin: 2 }} spacing={2}>
                <TextField
                    value={gift.name}
                    size="small"
                    onChange={(e) => updateGift({ name: e.target.value })}
                    label="Name"
                    required
                />

                <WrappingField
                    gameElements={gameElements}
                    wrapperGameElementId={gift.wrapperGameElementId}
                    onChange={(wrapperGameElementId) =>
                        updateGift({ wrapperGameElementId })
                    }
                />

                <GameElementPicker
                    label="Game Elements"
                    value={selectedGameElements}
                    size="small"
                    multiple
                    onChange={(elements) => {
                        if (elements) {
                            updateGift({
                                contents: elements.map((ge) => ge.id),
                            });
                        }
                    }}
                />

                <AdvancedOptions
                    gift={gift}
                    idLocked={Boolean(defaultValue?.id)}
                    onChange={updateGift}
                />

                <PublicationsSection
                    state={publicationState}
                    isNewGift={isNewGift}
                    canUpdate={Boolean(onUpdatePublication)}
                    canDelete={Boolean(onDeletePublication)}
                />

                <Stack direction="row" spacing={1}>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={!isUnsaved || missingDates}
                        type="submit"
                    >
                        Save
                    </Button>
                    <Button
                        onClick={(e) => {
                            e.preventDefault();
                            cancel();
                        }}
                    >
                        Cancel
                    </Button>
                </Stack>
            </Stack>
        </form>
    );
};
